import React, { useState } from 'react';

const SafetyModule = ({ onBack, currentUser }) => {
  const [currentSection, setCurrentSection] = useState('home'); // 'home', 'physical-emergency', 'property-damage'
  const [emergencyType, setEmergencyType] = useState(null); // 'major', 'minor', 'gas-line'
  const [checkedSteps, setCheckedSteps] = useState([]);

  const majorEmergencySteps = [
    {
      title: "Call 911 Immediately",
      description: "If there is any doubt, treat it as major.",
      icon: "🚨",
      urgent: true
    },
    {
      title: "Contact Supervisor or Manager",
      description: "Contact your Supervisor or Manager right away.",
      icon: "📞",
      urgent: true
    },
    {
      title: "Ensure Scene Safety",
      description: "Stop work in the area, keep others away.",
      icon: "⛔",
      urgent: true
    },
    {
      title: "Document the Incident",
      description: "Take photos/gather documentation of the incident and environment if safe to do so.",
      icon: "📸",
      urgent: false
    },
    {
      title: "File WSIB Injury Report",
      description: "Complete WSIB injury report with assistance from manager.",
      icon: "📋",
      urgent: false
    },
    {
      title: "Attend to Worker(s)",
      description: "Attend to worker(s) until emergency services arrive, if safe to do so.",
      icon: "🏥",
      urgent: true
    }
  ];

  const majorPostIncidentSteps = [
    {
      title: "Meet with Senior Staff",
      description: "Meet with senior staff to review the event.",
      icon: "👥",
      urgent: false
    },
    {
      title: "Identify Root Cause",
      description: "Identify root cause and document findings.",
      icon: "🔍",
      urgent: false
    },
    {
      title: "Update Procedures",
      description: "Create or update policy/procedures to prevent recurrence.",
      icon: "📝",
      urgent: false
    }
  ];

  const minorEmergencySteps = [
    {
      title: "Assess the Situation",
      description: "Carefully evaluate what has happened.",
      icon: "🔍",
      urgent: false
    },
    {
      title: "Confirm Minor Status",
      description: "Confirm it is truly minor (no immediate life-threat).",
      icon: "✅",
      urgent: true
    },
    {
      title: "Seek Medical Care",
      description: "Go to hospital/clinic as required or apply first aid.",
      icon: "🏥",
      urgent: false
    },
    {
      title: "Contact Supervisor",
      description: "Contact your Supervisor or Manager.",
      icon: "📞",
      urgent: false
    },
    {
      title: "Document Incident",
      description: "Take photos/gather documentation of the incident and environment.",
      icon: "📸",
      urgent: false
    },
    {
      title: "Complete WSIB Form",
      description: "Complete WSIB form if medical treatment is needed.",
      icon: "📋",
      urgent: false
    }
  ];

  const minorFollowUpSteps = [
    {
      title: "Support Worker",
      description: "Supervisor: Ensure worker is supported.",
      icon: "🤝",
      urgent: false
    },
    {
      title: "Review with Staff",
      description: "Supervisor: Review incident with staff.",
      icon: "👥",
      urgent: false
    },
    {
      title: "Update Safety Procedures",
      description: "Supervisor: Update safety procedures if necessary.",
      icon: "📝",
      urgent: false
    }
  ];

  const gasLineEmergencySteps = [
    {
      title: "Stop Work Immediately",
      description: "Halt all excavation or construction activity.",
      icon: "🛑",
      urgent: true
    },
    {
      title: "Shut Down Equipment",
      description: "Turn off engines, tools, and anything that could spark or ignite.",
      icon: "🔧",
      urgent: true
    },
    {
      title: "Do Not Attempt Repair",
      description: "Never try to fix or plug the gas line yourself.",
      icon: "⛔",
      urgent: true
    },
    {
      title: "Evacuate to Safe Location",
      description: "Move the crew and anyone nearby upwind of the leak. Stay at least 100 metres away if possible.",
      icon: "🏃‍♂️",
      urgent: true
    },  
    {
      title: "Contact Gas Company",
      description: "Enbridge Gas (Thunder Bay): 1-877-969-0999 (24/7). If unsure or unable to reach them, call 911.",
      icon: "📞",
      urgent: true
    },
    {
      title: "Contact Supervisor/Manager",
      description: "Report the incident immediately.",
      icon: "👨‍💼",
      urgent: true
    },
    {
      title: "Inform the Customer",
      description: "Let the property owner know the situation is being managed.",
      icon: "🏠",
      urgent: false
    },
    {
      title: "Secure the Site",
      description: "Keep people out of the danger area until emergency responders arrive.",
      icon: "🚧",
      urgent: false
    },
    {
      title: "Document the Incident",
      description: "Take photos and notes only once the site is safe. Submit details to the office.",
      icon: "📋",
      urgent: false
    },
    {
      title: "Post-Incident Review",
      description: "Management and crew to meet, assess cause, and implement preventive measures.",
      icon: "📊",
      urgent: false
    }
  ];

  const waterLineEmergencySteps = [
    {
      title: "Stop All Work Immediately",
      description: "Halt all excavation or construction activity.",
      icon: "🛑",
      urgent: true
    },
    {
      title: "Shut Down Machine & Clear Area",
      description: "Shut down the machine/excavator and clear the immediate area.",
      icon: "🚜",
      urgent: true
    },
    {
      title: "Control the Water if Possible",
      description: "Use the excavator bucket or materials on site to block or redirect water. Crimp or pinch the waterline if safe and practical.",
      icon: "🌊",
      urgent: true
    },
    {
      title: "Install Pump if Flooding",
      description: "Set up a trash pump or water pump to lower water levels. Discharge safely to a ditch or storm sewer as allowed.",
      icon: "⚙️",
      urgent: true
    },
    {
      title: "Contact Municipality",
      description: "City of Thunder Bay Water Emergency (24/7): 807-684-3111. If outside Thunder Bay, call local municipal water department.",
      icon: "📞",
      urgent: true
    },
    {
      title: "Arrange for Repair",
      description: "Contact a licensed plumbing company to repair the damaged line.",
      icon: "🔧",
      urgent: false
    },
    {
      title: "Contact Supervisor/Manager",
      description: "Report the incident right away.",
      icon: "👨‍💼",
      urgent: false
    },
    {
      title: "Inform the Customer",
      description: "Let the property owner know the situation and next steps.",
      icon: "🏠",
      urgent: false
    },
    {
      title: "Secure the Site",
      description: "Ensure area is safe from flooding, erosion, or further damage. Keep workers and bystanders clear until water is under control.",
      icon: "🚧",
      urgent: false
    },
    {
      title: "Document the Incident",
      description: "Take photos/videos once the site is safe. Record details for insurance and internal records.",
      icon: "📋",
      urgent: false
    },
    {
      title: "Post-Incident Review",
      description: "Management and crew meet to review cause, discuss preventive measures, and update procedures.",
      icon: "📊",
      urgent: false
    }
  ];

  const electricalEmergencySteps = [
    {
      title: "Stop All Work Immediately",
      description: "Halt all work and do not touch any equipment, lines, or water near the hazard.",
      icon: "🛑",
      urgent: true
    },
    {
      title: "Shut Down Equipment if Safe",
      description: "Only power down machinery or breakers if it can be done without risk of contact with electricity. Otherwise, leave the area.",
      icon: "🔌",
      urgent: true
    },
    {
      title: "Keep Clear of Hazard",
      description: "Stay at least 10 metres (33 feet) away from downed lines, exposed wires, or energized equipment. Treat all wires as live.",
      icon: "⚡",
      urgent: true
    },
    {
      title: "Do Not Attempt Repair",
      description: "Never try to fix or move electrical equipment, lines, or components yourself.",
      icon: "⛔",
      urgent: true
    },
    {
      title: "Call Emergency Services if Required",
      description: "If a line is sparking, has fallen, or is causing fire → Call 911 immediately.",
      icon: "🚨",
      urgent: true
    },
    {
      title: "Contact Local Utility Company",
      description: "Thunder Bay Hydro (24/7 Outage Line): 807-343-1111. For other municipalities, call local hydro company emergency line.",
      icon: "📞",
      urgent: true
    },
    {
      title: "Contact Supervisor/Manager",
      description: "Report the incident immediately.",
      icon: "👨‍💼",
      urgent: false
    },
    {
      title: "Inform the Customer",
      description: "Advise the property owner of the hazard and actions being taken.",
      icon: "🏠",
      urgent: false
    },
    {
      title: "Secure the Site",
      description: "Keep people and equipment out of danger area. If at night, use cones/lights to mark off danger zone (from a safe distance).",
      icon: "🚧",
      urgent: false
    },
    {
      title: "If Injury Occurs",
      description: "Do not touch the injured person until power source is confirmed de-energized. Call 911 immediately. Begin first aid/CPR only once safe.",
      icon: "🏥",
      urgent: true,
      special: true
    },
    {
      title: "Document the Incident",
      description: "Take photos and notes once the hazard has been neutralized. File details with office for records.",
      icon: "📋",
      urgent: false
    },
    {
      title: "Post-Incident Review",
      description: "Management and crew meet to review root cause, update procedures, and reinforce safe work practices.",
      icon: "📊",
      urgent: false
    }
  ];

  const fireEmergencySteps = [
    {
      title: "Stop All Work Immediately",
      description: "Halt all work activities immediately.",
      icon: "🛑",
      urgent: true
    },
    {
      title: "Shut Down Equipment if Safe",
      description: "Shut down equipment if safe to do so without delay.",
      icon: "🔧",
      urgent: true
    },
    {
      title: "Raise the Alarm",
      description: "Shout 'FIRE!' and alert everyone on site.",
      icon: "📢",
      urgent: true
    },
    {
      title: "Evacuate the Area Immediately",
      description: "Get everyone out of the area immediately.",
      icon: "🏃‍♂️",
      urgent: true
    },
    {
      title: "Call 911",
      description: "Report the fire, location, and type of materials burning if known.",
      icon: "🚨",
      urgent: true
    },
    {
      title: "Attempt to Extinguish Only if Safe",
      description: "Use fire extinguisher only if: fire is small/contained, you have safe exit behind you, you are trained. Class A (wood/paper): water/foam/dry chemical. Class B (fuel/oil): foam/CO₂/dry chemical, never water. Class C (electrical): CO₂/dry chemical, never water. If any doubt, do not attempt — evacuate.",
      icon: "🧯",
      urgent: true
    },
    {
      title: "Move to Safe Assembly Point",
      description: "Gather crew upwind and away from the building or fire.",
      icon: "👥",
      urgent: true
    },
    {
      title: "Account for All Workers",
      description: "Ensure all crew members are accounted for and safe.",
      icon: "✅",
      urgent: true
    },
    {
      title: "Contact Supervisor/Manager",
      description: "Report the incident immediately.",
      icon: "👨‍💼",
      urgent: false
    },
    {
      title: "Inform the Customer",
      description: "Advise property owner of the fire and that emergency services are on the way.",
      icon: "🏠",
      urgent: false
    },
    {
      title: "Secure the Site",
      description: "Prevent re-entry until cleared by fire services.",
      icon: "🚧",
      urgent: false
    },
    {
      title: "Document the Incident",
      description: "Once safe, take photos and record details of the event. File reports for insurance, WSIB (if injuries), and internal records.",
      icon: "📋",
      urgent: false
    },
    {
      title: "Post-Incident Review",
      description: "Management and crew meet to determine root cause. Update policies, equipment (extinguishers, alarms), and procedures for prevention.",
      icon: "📊",
      urgent: false
    }
  ];

  const structuralEmergencySteps = [
    {
      title: "Stop All Work Immediately",
      description: "Halt all work activities immediately upon suspected or active structural failure.",
      icon: "🛑",
      urgent: true
    },
    {
      title: "Evacuate Structure & Danger Zone",
      description: "Evacuate the structure and surrounding danger zone immediately.",
      icon: "🏃‍♂️",
      urgent: true
    },
    {
      title: "Account for All Crew Members",
      description: "Account for all crew members and ensure no one re-enters until cleared.",
      icon: "✅",
      urgent: true
    },
    {
      title: "Set Up Barricades",
      description: "Set up barricades, caution tape, or fencing to keep people out.",
      icon: "🚧",
      urgent: true
    },
    {
      title: "Establish Safe Perimeter",
      description: "Establish a safe perimeter based on the size and condition of the structure.",
      icon: "📏",
      urgent: true
    },
    {
      title: "Assign Area Spotter",
      description: "Assign a crew member to act as a spotter to monitor the area.",
      icon: "👁️",
      urgent: true
    },
    {
      title: "Emergency Stabilization (If Safe)",
      description: "If possible without endangering anyone, use temporary shoring, supports, or bracing to reduce risk of further collapse. Relieve pressure on compromised areas only if safe. Do not attempt full repairs—only emergency stabilization.",
      icon: "🏗️",
      urgent: false
    },
    {
      title: "Benchmark the Structure",
      description: "Benchmark the structure using reference points, strings, or markers to detect movement.",
      icon: "📐",
      urgent: false
    },
    {
      title: "Document Observations",
      description: "Document observations with photos and notes for engineers and supervisors.",
      icon: "📸",
      urgent: false
    },
    {
      title: "Contact Supervisor/Manager",
      description: "Contact supervisor/manager immediately.",
      icon: "👨‍💼",
      urgent: true
    },
    {
      title: "Inform Homeowner/Client",
      description: "Inform homeowner/client of the situation.",
      icon: "🏠",
      urgent: false
    },
    {
      title: "Call Engineering Support",
      description: "Call engineering support to assess structural stability before re-entry.",
      icon: "👷‍♂️",
      urgent: true
    },
    {
      title: "Contact Emergency Services if Required",
      description: "If public safety is at risk (street, neighbour's property, utilities), contact municipal emergency services.",
      icon: "🚨",
      urgent: true
    },
    {
      title: "Record All Actions Taken",
      description: "Record all actions taken, including time, crew involved, and stabilization measures.",
      icon: "📝",
      urgent: false
    },
    {
      title: "Take Before/After Photos",
      description: "Take before/after photos of the area.",
      icon: "📷",
      urgent: false
    },
    {
      title: "File Incident Report",
      description: "File an incident report with management.",
      icon: "📋",
      urgent: false
    },
    {
      title: "Wait for Engineering Clearance",
      description: "Work can only restart after a qualified engineer or supervisor has cleared the site.",
      icon: "🔍",
      urgent: false
    },
    {
      title: "Review Temporary Measures",
      description: "All temporary measures must be reviewed and approved before continuing work.",
      icon: "✔️",
      urgent: false
    }
  ];

  const getAllSteps = () => {
    if (emergencyType === 'major') {
      return [...majorEmergencySteps, ...majorPostIncidentSteps];
    } else if (emergencyType === 'minor') {
      return [...minorEmergencySteps, ...minorFollowUpSteps];
    } else if (emergencyType === 'gas-line') {
      return gasLineEmergencySteps;
    } else if (emergencyType === 'water-line') {
      return waterLineEmergencySteps;
    } else if (emergencyType === 'electrical') {
      return electricalEmergencySteps;
    } else if (emergencyType === 'fire') {
      return fireEmergencySteps;
    } else if (emergencyType === 'structural') {
      return structuralEmergencySteps;
    }
    return [];
  };

  const handleStepToggle = (stepIndex) => {
    if (checkedSteps.includes(stepIndex)) {
      setCheckedSteps(checkedSteps.filter(index => index !== stepIndex));
    } else {
      setCheckedSteps([...checkedSteps, stepIndex]);
    }
  };

  const handleReset = () => {
    setCheckedSteps([]);
    setEmergencyType(null);
    setCurrentSection('home');
  };

  const getProgressPercentage = () => {
    const totalSteps = getAllSteps().length;
    return totalSteps > 0 ? Math.round((checkedSteps.length / totalSteps) * 100) : 0;
  };

  // Safety Home Page
  if (currentSection === 'home') {
    return (
      <div className="min-h-screen bg-gray-50 text-gray-800">
        <div className="container mx-auto px-4 py-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <button onClick={onBack} className="text-blue-600 hover:text-blue-700 text-2xl">← Back</button>
            <h1 className="text-4xl font-bold text-gray-800">⚠️ SAFETY CENTER</h1>
            <div className="w-20"></div>
          </div>

          {/* Safety Categories */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            
            {/* Physical Emergency */}
            <div 
              className="bg-white rounded-xl p-8 cursor-pointer transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl border border-gray-200"
              onClick={() => {
                setEmergencyType(null);
                setCurrentSection('physical-emergency');
              }}
            >
              <div className="text-center">
                <div className="text-6xl mb-4">🚨</div>
                <h2 className="text-2xl font-bold mb-2 text-gray-800">PHYSICAL EMERGENCY</h2>
                <p className="text-gray-600 text-sm">Major & minor incident procedures</p>
              </div>
            </div>

            {/* Gas Line Emergency */}
            <div 
              className="bg-white rounded-xl p-8 cursor-pointer transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl border border-gray-200"
              onClick={() => {
                setCurrentSection('');
                setEmergencyType('gas-line');
                setCheckedSteps([]);
              }}
            >
              <div className="text-center">
                <div className="text-6xl mb-4">⛽</div>
                <h2 className="text-2xl font-bold mb-2 text-gray-800">GAS LINE EMERGENCY</h2>
                <p className="text-gray-600 text-sm">Gas leaks & utility damage</p>
              </div>
            </div>

            {/* Waterline Emergency */}
            <div 
              className="bg-white rounded-xl p-8 cursor-pointer transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl border border-gray-200"
              onClick={() => {
                setCurrentSection('');
                setEmergencyType('water-line');
                setCheckedSteps([]);
              }}
            >
              <div className="text-center">
                <div className="text-6xl mb-4">🚰</div>
                <h2 className="text-2xl font-bold mb-2 text-gray-800">WATERLINE EMERGENCY</h2>
                <p className="text-gray-600 text-sm">Water damage & flooding</p>
              </div>
            </div>

            {/* Electrical Emergency */}
            <div 
              className="bg-white rounded-xl p-8 cursor-pointer transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl border border-gray-200"
              onClick={() => {
                setCurrentSection('');
                setEmergencyType('electrical');
                setCheckedSteps([]);
              }}
            >
              <div className="text-center">
                <div className="text-6xl mb-4">⚡</div>
                <h2 className="text-2xl font-bold mb-2 text-gray-800">ELECTRICAL EMERGENCY</h2>
                <p className="text-gray-600 text-sm">Power lines & electrical hazards</p>
              </div>
            </div>

            {/* Fire Emergency */}
            <div 
              className="bg-white rounded-xl p-8 cursor-pointer transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl border border-gray-200"
              onClick={() => {
                setCurrentSection('');
                setEmergencyType('fire');
                setCheckedSteps([]);
              }}
            >
              <div className="text-center">
                <div className="text-6xl mb-4">🔥</div>
                <h2 className="text-2xl font-bold mb-2 text-gray-800">FIRE EMERGENCY</h2>
                <p className="text-gray-600 text-sm">Fire suppression & evacuation</p>
              </div>
            </div>

            {/* Structural Emergency */}
            <div 
              className="bg-white rounded-xl p-8 cursor-pointer transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl border border-gray-200"
              onClick={() => {
                setCurrentSection('');
                setEmergencyType('structural');
                setCheckedSteps([]);
              }}
            >
              <div className="text-center">
                <div className="text-6xl mb-4">🏗️</div>
                <h2 className="text-2xl font-bold mb-2 text-gray-800">STRUCTURAL EMERGENCY</h2>
                <p className="text-gray-600 text-sm">Collapse & structural failure</p>
              </div>
            </div>

          </div>
        </div>
      </div>
    );
  }

  // Physical Emergency Section
  if (currentSection === 'physical-emergency' && !emergencyType) {
    return (
      <div className="min-h-screen bg-gray-50 text-gray-800">
        <div className="container mx-auto px-4 py-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <button onClick={() => setCurrentSection('home')} className="text-blue-600 hover:text-blue-700 text-2xl">← Back</button>
            <h1 className="text-4xl font-bold text-gray-800">🚨 PHYSICAL EMERGENCY</h1>
            <div className="w-20"></div>
          </div>

          {/* Emergency Type Selection */}
          <div className="max-w-4xl mx-auto">
            <div className="bg-yellow-100 border-l-4 border-yellow-500 p-6 mb-8">
              <div className="flex">
                <div className="text-yellow-500 text-2xl mr-3">⚠️</div>
                <div>
                  <h3 className="text-lg font-bold text-yellow-800 mb-2">Emergency Assessment</h3>
                  <p className="text-yellow-700">Choose the type of emergency you are dealing with. If there is any doubt, treat it as MAJOR.</p>
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              
              {/* Major Emergency */}
              <div 
                className="bg-red-50 border-2 border-red-200 rounded-xl p-8 cursor-pointer transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
                onClick={() => {
                  setCurrentSection('');
                  setEmergencyType('major');
                  setCheckedSteps([]);
                }}
              >
                <div className="text-center">
                  <div className="text-8xl mb-4">🚨</div>
                  <h2 className="text-3xl font-bold mb-4 text-red-800">MAJOR EMERGENCY</h2>
                  <div className="text-left space-y-2 text-red-700">
                    <p className="font-semibold">• Life-threatening injury</p>
                    <p className="font-semibold">• Severe injury requiring immediate medical attention</p>
                    <p className="font-semibold">• Unconsciousness</p>
                    <p className="font-semibold">• Heavy bleeding</p>
                    <p className="font-semibold">• When in doubt</p>
                  </div>
                  <div className="mt-6 bg-red-600 text-white py-3 px-6 rounded-lg font-bold">
                    START MAJOR EMERGENCY PROTOCOL
                  </div>
                </div>
              </div>

              {/* Minor Emergency */}
              <div 
                className="bg-orange-50 border-2 border-orange-200 rounded-xl p-8 cursor-pointer transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
                onClick={() => {
                  setCurrentSection('');
                  setEmergencyType('minor');
                  setCheckedSteps([]);
                }}
              >
                <div className="text-center">
                  <div className="text-8xl mb-4">🩹</div>
                  <h2 className="text-3xl font-bold mb-4 text-orange-800">MINOR EMERGENCY</h2>
                  <div className="text-left space-y-2 text-orange-700">
                    <p className="font-semibold">• Minor cuts or scrapes</p>
                    <p className="font-semibold">• Small burns</p>
                    <p className="font-semibold">• Minor sprains</p>
                    <p className="font-semibold">• No immediate life threat</p>
                    <p className="font-semibold">• Conscious and alert</p>
                  </div>
                  <div className="mt-6 bg-orange-600 text-white py-3 px-6 rounded-lg font-bold">
                    START MINOR EMERGENCY PROTOCOL
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    );
  }

  // Emergency Checklist Protocol
  if (emergencyType) {
    const allSteps = getAllSteps();
    
    // Get emergency type display info
    const getEmergencyInfo = () => {
      switch (emergencyType) {
        case 'major':
          return { title: '🚨 MAJOR EMERGENCY PROTOCOL', color: 'red' };
        case 'minor':
          return { title: '🩹 MINOR EMERGENCY PROTOCOL', color: 'orange' };
        case 'gas-line':
          return { title: '⛽ GAS LINE EMERGENCY PROTOCOL', color: 'orange' };
        case 'water-line':
          return { title: '🚰 WATERLINE EMERGENCY PROTOCOL', color: 'blue' };
        case 'electrical':
          return { title: '⚡ ELECTRICAL EMERGENCY PROTOCOL', color: 'yellow' };
        case 'fire':
          return { title: '🔥 FIRE EMERGENCY PROTOCOL', color: 'red' };
        case 'structural':
          return { title: '🏗️ STRUCTURAL EMERGENCY PROTOCOL', color: 'purple' };
        default:
          return { title: 'EMERGENCY PROTOCOL', color: 'gray' };
      }
    };

    const emergencyInfo = getEmergencyInfo();

    return (
      <div className="min-h-screen bg-gray-50 text-gray-800">
        <div className="container mx-auto px-4 py-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <button onClick={handleReset} className="text-blue-600 hover:text-blue-700 text-2xl">← Back</button>
            <h1 className="text-3xl font-bold text-gray-800">{emergencyInfo.title}</h1>
            <div className="text-right">
              <p className="text-lg font-semibold text-gray-700">
                {checkedSteps.length} of {allSteps.length} Complete
              </p>
              <div className="text-sm text-gray-500">{getProgressPercentage()}% Complete</div>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="max-w-6xl mx-auto mb-8">
            <div className="bg-gray-200 rounded-full h-3">
              <div 
                className={`h-3 rounded-full transition-all duration-500 ${
                  emergencyInfo.color === 'red' ? 'bg-red-500' : 
                  emergencyInfo.color === 'blue' ? 'bg-blue-500' : 
                  emergencyInfo.color === 'yellow' ? 'bg-yellow-500' : 
                  emergencyInfo.color === 'purple' ? 'bg-purple-500' : 'bg-orange-500'
                }`}
                style={{ width: `${getProgressPercentage()}%` }}
              ></div>
            </div>
          </div>

          {/* Emergency Checklist */}
          <div className="max-w-6xl mx-auto">
            
            {/* Instructions */}
            <div className={`border-l-4 p-6 mb-8 ${
              emergencyInfo.color === 'red' 
                ? 'bg-red-50 border-red-500' 
                : emergencyInfo.color === 'blue'
                ? 'bg-blue-50 border-blue-500'
                : emergencyInfo.color === 'yellow'
                ? 'bg-yellow-50 border-yellow-500'
                : emergencyInfo.color === 'purple'
                ? 'bg-purple-50 border-purple-500'
                : 'bg-orange-50 border-orange-500'
            }`}>
              <div className="flex">
                <div className={`text-2xl mr-3 ${
                  emergencyInfo.color === 'red' ? 'text-red-500' : 
                  emergencyInfo.color === 'blue' ? 'text-blue-500' : 
                  emergencyInfo.color === 'yellow' ? 'text-yellow-500' : 
                  emergencyInfo.color === 'purple' ? 'text-purple-500' : 'text-orange-500'
                }`}>⚠️</div>
                <div>
                  <h3 className={`text-lg font-bold mb-2 ${
                    emergencyInfo.color === 'red' ? 'text-red-800' : 
                    emergencyInfo.color === 'blue' ? 'text-blue-800' : 
                    emergencyInfo.color === 'yellow' ? 'text-yellow-800' : 
                    emergencyInfo.color === 'purple' ? 'text-purple-800' : 'text-orange-800'
                  }`}>Emergency Protocol Checklist</h3>
                  <p className={emergencyInfo.color === 'red' ? 'text-red-700' : 
                    emergencyInfo.color === 'blue' ? 'text-blue-700' : 
                    emergencyInfo.color === 'yellow' ? 'text-yellow-700' : 
                    emergencyInfo.color === 'purple' ? 'text-purple-700' : 'text-orange-700'}>
                    Check off each step as you complete it. Priority steps are marked as URGENT and should be completed first.
                  </p>
                </div>
              </div>
            </div>

            {/* Additional Safety Notes for Waterline Emergency */}
            {emergencyType === 'water-line' && (
              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6 mb-8">
                <div className="flex">
                  <div className="text-yellow-500 text-2xl mr-3">⚠️</div>
                  <div>
                    <h3 className="text-lg font-bold text-yellow-800 mb-2">Additional Safety Notes:</h3>
                    <p className="text-yellow-700 font-semibold">
                      If water comes into contact with electrical systems, treat as an electrical hazard and keep out until cleared safe.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Key Reminders for Electrical Emergency */}
            {emergencyType === 'electrical' && (
              <div className="bg-red-50 border-l-4 border-red-500 p-6 mb-8">
                <div className="flex">
                  <div className="text-red-500 text-2xl mr-3">⚡</div>
                  <div>
                    <h3 className="text-lg font-bold text-red-800 mb-2">⚡ Key Reminders:</h3>
                    <div className="space-y-2 text-red-700 font-semibold">
                      <p>• Water + electricity = extreme hazard. If water is present, treat as a Major Emergency and wait for professionals.</p>
                      <p>• Always assume lines and equipment are live until confirmed by the utility company.</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Key Reminders for Fire Emergency */}
            {emergencyType === 'fire' && (
              <div className="bg-red-50 border-l-4 border-red-500 p-6 mb-8">
                <div className="flex">
                  <div className="text-red-500 text-2xl mr-3">🔥</div>
                  <div>
                    <h3 className="text-lg font-bold text-red-800 mb-2">🔥 Key Reminders:</h3>
                    <div className="space-y-2 text-red-700 font-semibold">
                      <p>• Never use water on electrical or fuel fires.</p>
                      <p>• Always prioritize life over property.</p>
                      <p>• If the fire grows or spreads, evacuate — leave it to professionals.</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Golden Rule for Structural Emergency */}
            {emergencyType === 'structural' && (
              <div className="bg-red-50 border-l-4 border-red-500 p-6 mb-8">
                <div className="flex">
                  <div className="text-red-500 text-2xl mr-3">🦫</div>
                  <div>
                    <h3 className="text-lg font-bold text-red-800 mb-2">⚠️ Golden Rule:</h3>
                    <p className="text-red-700 font-bold text-lg">
                      Never prioritize the structure over human safety. Structures can be repaired or replaced—lives cannot.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Checklist Items */}
            <div className="space-y-4">
              {allSteps.map((step, index) => {
                const isChecked = checkedSteps.includes(index);
                const isUrgent = step.urgent;
                const isSpecial = step.special; // For injury step in electrical
                
                return (
                  <div
                    key={index}
                    className={`border-2 rounded-xl p-6 transition-all duration-300 cursor-pointer ${
                      isChecked
                        ? 'bg-green-50 border-green-300 shadow-md'
                        : isSpecial
                        ? 'bg-red-50 border-red-300 hover:border-red-400' // Special styling for injury step
                        : isUrgent
                        ? emergencyInfo.color === 'red'
                          ? 'bg-red-50 border-red-200 hover:border-red-300'
                          : emergencyInfo.color === 'blue'
                          ? 'bg-blue-50 border-blue-200 hover:border-blue-300'
                          : emergencyInfo.color === 'yellow'
                          ? 'bg-yellow-50 border-yellow-200 hover:border-yellow-300'
                          : emergencyInfo.color === 'purple'
                          ? 'bg-purple-50 border-purple-200 hover:border-purple-300'
                          : 'bg-orange-50 border-orange-200 hover:border-orange-300'
                        : 'bg-white border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => handleStepToggle(index)}
                  >
                    <div className="flex items-start space-x-4">
                      
                      {/* Checkbox */}
                      <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center transition-all duration-300 ${
                        isChecked
                          ? 'bg-green-500 border-green-500 text-white'
                          : 'border-gray-300 hover:border-gray-400'
                      }`}>
                        {isChecked && '✓'}
                      </div>

                      {/* Step Icon */}
                      <div className="text-4xl">{step.icon}</div>

                      {/* Step Content */}
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className={`text-xl font-bold ${
                            isChecked ? 'text-green-800' : 'text-gray-800'
                          }`}>
                            {step.title}
                          </h3>
                          
                          {/* Special Badge for Injury Step */}
                          {isSpecial && !isChecked && (
                            <span className="px-3 py-1 rounded-full text-xs font-bold bg-red-600 text-white animate-pulse">
                              CRITICAL
                            </span>
                          )}
                          
                          {/* Urgent Badge */}
                          {isUrgent && !isSpecial && !isChecked && (
                            <span className={`px-3 py-1 rounded-full text-xs font-bold text-white ${
                              emergencyInfo.color === 'red' ? 'bg-red-600' : 
                              emergencyInfo.color === 'blue' ? 'bg-blue-600' :
                              emergencyInfo.color === 'yellow' ? 'bg-yellow-600' :
                              emergencyInfo.color === 'purple' ? 'bg-purple-600' : 'bg-orange-600'
                            }`}>
                              URGENT
                            </span>
                          )}
                          
                          {/* Completed Badge */}
                          {isChecked && (
                            <span className="px-3 py-1 rounded-full text-xs font-bold bg-green-600 text-white">
                              COMPLETED
                            </span>
                          )}
                        </div>
                        
                        <p className={`text-lg ${
                          isChecked ? 'text-green-700' : 'text-gray-700'
                        }`}>
                          {step.description}
                        </p>
                      </div>

                    </div>
                  </div>
                );
              })}
            </div>

            {/* Completion Summary */}
            {checkedSteps.length === allSteps.length && (
              <div className="mt-8 bg-green-100 border-2 border-green-300 rounded-xl p-8 text-center">
                <div className="text-6xl mb-4">✅</div>
                <h2 className="text-2xl font-bold text-green-800 mb-4">ALL STEPS COMPLETED</h2>
                <p className="text-green-700 mb-6">
                  You have successfully completed all steps in the {emergencyType.replace('-', ' ')} emergency protocol. 
                  All required actions have been documented and checked off.
                </p>
                <div className="space-x-4">
                  <button
                    onClick={handleReset}
                    className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-bold"
                  >
                    Return to Safety Center
                  </button>
                  <button
                    onClick={() => setCheckedSteps([])}
                    className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-3 rounded-lg font-bold"
                  >
                    Reset Checklist
                  </button>
                </div>
              </div>
            )}

            {/* Progress Summary */}
            {checkedSteps.length > 0 && checkedSteps.length < allSteps.length && (
              <div className="mt-8 bg-blue-50 border border-blue-200 rounded-xl p-6">
                <h3 className="text-lg font-bold text-blue-800 mb-2">
                  Progress: {checkedSteps.length} of {allSteps.length} steps completed ({getProgressPercentage()}%)
                </h3>
                <p className="text-blue-700 text-sm">
                  Continue checking off steps as you complete them. Urgent steps should be prioritized.
                </p>
              </div>
            )}

          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default SafetyModule;
